
import styles from "./HeroPanel.module.css";
import { AnimatePresence, motion } from "framer-motion";
import { useHeroContext } from ".";
import _ from "lodash";
import { ContentOverlayBlock } from "./components";
import classnames from "classnames";
import { useState } from "react";

export interface HeroPanelProps {
  primary?: boolean,
  subPage?: { [key: string]: string | number },
  className?: string;
  children: React.ReactNode;
  index?: number;
  style?: {};
  active?: boolean;
  textonly?: boolean;
}

export const HeroPanel = ({
  primary,
  subPage,
  children,
  className = "",
  active = false,
  textonly = false,
  index,
  ...props
}: HeroPanelProps) => {
  const {
    type,
    ogConfig,
    animationConfig,
    handleSetAnimationConfig,
    hoverConfigs,
    activeIndex,
    handleSetActiveIndex,
    childrenLen,
    indexState,
    handleSetIndexState
  } = useHeroContext();

  /* Component State */
  const [stopHover, setStopHover] = useState(false);

  const isActive = activeIndex === index;
  // Mark the first slide index visible in Hero.
  const marker = childrenLen > 6 ? 3 : 2;

  /**
   * helper fn that creates a new index array
   */
  const handleSwop = (idx: number) => {
    const skipSlides = indexState.indexOf(idx) - marker;
    const panels = indexState.slice(skipSlides, indexState.length).concat(indexState.slice(0, skipSlides));
    return panels;
  };

  /**
   * Click event to toggle the 'open' state of this panel
   */
  const toggleActive = (idx: number) => {
    const data = handleSwop(idx);
    setStopHover(true);

    setTimeout(() => {
      handleSetIndexState(data)
      handleSetActiveIndex(idx)
      setStopHover(false);
    }, 600)
  };

  // Debounce fn prevents too many fn-calls being made at once
  const useToggleClickDebounce = (idx: number) => _.debounce(() => toggleActive(idx), 200);

  // Return index of clicked panel in the "indexState" array
  const pullIndex = (idx: number) => {
    const i = indexState.indexOf(idx);
    return i;
  }

  /**
   * Update panels positioning on panel hover
  */
  const handleMouseEnter = (idx: number) => {
    if (hoverConfigs && idx !== activeIndex) {
      const figCaptions = document.querySelectorAll('.hero-figcaption');
      figCaptions.forEach(n => n.classList.add('unq-hidden'));

      if (hoverConfigs.length === 2) {
        let config = activeIndex + 1 === idx ? hoverConfigs[0] : hoverConfigs[1];
        if (idx === 0) {
          const i = indexState.indexOf(activeIndex);
          config = indexState[i + 1] === idx ? hoverConfigs[0] : hoverConfigs[1];
        }

        handleSetAnimationConfig(config);

      } else {
        handleSetAnimationConfig(hoverConfigs);
      }
    }
  };

  /**
 * Update panels positioning on panel mouseLeave
*/
  const handleMouseLeave = () => {
    const figCaptions = document.querySelectorAll('.hero-figcaption');
    figCaptions.forEach(n => n.classList.remove('unq-hidden'));
    
    if (hoverConfigs && animationConfig !== ogConfig) {
      handleSetAnimationConfig(ogConfig);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        id={`hero-panel-${index}`}
        className={classnames(
          "heropanel",
          type,
          (index === activeIndex) && styles.open,
          { active: index === activeIndex },
          styles.heropanel,
          textonly && styles.textonly,
          className
        )}
        whileHover={type === 'slide' ? {
          originX: 0,
          x: isActive || primary ? 0 : '-10%',
          transition: { duration: .5 }
        } : null}
        animate={type === 'slide' ? { ...animationConfig[index], width: isActive ? '52%' : '16%' } :
          { ...animationConfig[pullIndex(index)] }
        }
        transition={{ type: "spring", damping: 20, bounce: 0 }}
        onClick={useToggleClickDebounce(index)}
        onMouseEnter={() => type === 'shuffle' ? handleMouseEnter(index) : null}
        onMouseLeave={() => type === 'shuffle' ? handleMouseLeave() : null}
        {...props}
      >

        {subPage &&
          <ContentOverlayBlock
            key={`overlay-block-${subPage.Title}`}
            blur={!isActive}
            p={subPage}
            activePanel={isActive} />}
        {children}
      </motion.div>
    </AnimatePresence>
  );
};
